import { useState, useEffect, useRef } from "react";
import { api } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { Building2, Upload, Save, Image, Phone, Mail, Globe, MapPin, Euro } from "lucide-react";

const CompanySettingsPage = () => {
  const [settings, setSettings] = useState({
    company_name: "",
    street: "",
    postal_code: "",
    city: "",
    phone: "",
    email: "",
    website: "",
    logo_url: "",
    logo_size: 50,
    stundenlohn: 0,
    tax_rate: 19
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get("/api/company-settings");
      setSettings({
        company_name: res.data.company_name || "",
        street: res.data.street || "",
        postal_code: res.data.postal_code || "",
        city: res.data.city || "",
        phone: res.data.phone || "",
        email: res.data.email || "",
        website: res.data.website || "",
        logo_url: res.data.logo_url || "",
        logo_size: res.data.logo_size || 50,
        stundenlohn: res.data.stundenlohn || 0,
        tax_rate: res.data.tax_rate != null ? res.data.tax_rate : 19
      });
      if (res.data.logo_url) {
        setLogoPreview(res.data.logo_url);
      }
    } catch (err) {
      toast.error("Fehler beim Laden der Einstellungen");
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Bitte nur Bilddateien hochladen");
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Datei zu groß. Maximal 2MB erlaubt.");
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result;
      setLogoPreview(base64);
      setSettings(prev => ({ ...prev, logo_url: base64 }));
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/api/company-settings", settings);
      toast.success("Einstellungen gespeichert!");
    } catch (err) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const removeLogo = () => {
    setLogoPreview(null);
    setSettings(prev => ({ ...prev, logo_url: "" }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="company-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
          Firmeneinstellungen
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Personalisieren Sie die App mit Ihrem Firmennamen und Logo
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Logo Upload */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Image className="w-5 h-5" />
              Firmenlogo
            </CardTitle>
            <CardDescription>
              Wird auf PDFs und in der App angezeigt
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-col items-center gap-6">
              {/* Logo Preview - Much larger */}
              <div className="w-80 h-80 border-2 border-dashed rounded-xl flex items-center justify-center bg-muted/30 overflow-hidden p-4">
                {logoPreview ? (
                  <img 
                    src={logoPreview} 
                    alt="Firmenlogo" 
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <div className="text-center text-muted-foreground">
                    <Building2 className="w-20 h-20 mx-auto mb-3 opacity-50" />
                    <span className="text-base">Kein Logo hochgeladen</span>
                    <p className="text-xs mt-1">Klicken Sie unten auf "Logo hochladen"</p>
                  </div>
                )}
              </div>

              {/* Logo Size Slider */}
              {logoPreview && (
                <div className="w-full max-w-xs space-y-3">
                  <Label className="text-sm">Logo-Größe im PDF: {settings.logo_size}mm</Label>
                  <Slider
                    value={[settings.logo_size]}
                    onValueChange={(value) => setSettings({ ...settings, logo_size: value[0] })}
                    min={20}
                    max={80}
                    step={5}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Klein (20mm)</span>
                    <span>Groß (80mm)</span>
                  </div>
                </div>
              )}

              {/* Upload Button */}
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  data-testid="logo-upload-input"
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="gap-2"
                >
                  <Upload className="w-4 h-4" />
                  Logo hochladen
                </Button>
                {logoPreview && (
                  <Button
                    variant="ghost"
                    onClick={removeLogo}
                    className="text-destructive"
                  >
                    Entfernen
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground text-center">
                PNG, JPG oder SVG. Max 2MB.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Company Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Firmendaten
            </CardTitle>
            <CardDescription>
              Diese Daten erscheinen auf Ihren Ausdrucken
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Firmenname *</Label>
              <Input
                value={settings.company_name}
                onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                placeholder="z.B. Mustermann KFZ-Service"
                data-testid="company-name-input"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Phone className="w-3 h-3" /> Telefon
                </Label>
                <Input
                  value={settings.phone}
                  onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                  placeholder="z.B. 0123 456789"
                  data-testid="company-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Mail className="w-3 h-3" /> E-Mail
                </Label>
                <Input
                  type="email"
                  value={settings.email}
                  onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                  placeholder="info@firma.de"
                  data-testid="company-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <Globe className="w-3 h-3" /> Website
              </Label>
              <Input
                value={settings.website}
                onChange={(e) => setSettings({ ...settings, website: e.target.value })}
                placeholder="www.meine-werkstatt.de"
                data-testid="company-website-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Address */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Adresse
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-3 space-y-2">
                <Label>Straße</Label>
                <Input
                  value={settings.street}
                  onChange={(e) => setSettings({ ...settings, street: e.target.value })}
                  placeholder="Musterstraße 123"
                  data-testid="company-street-input"
                />
              </div>
              <div className="space-y-2">
                <Label>PLZ</Label>
                <Input
                  value={settings.postal_code}
                  onChange={(e) => setSettings({ ...settings, postal_code: e.target.value })}
                  placeholder="12345"
                  data-testid="company-postal-input"
                />
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label>Stadt</Label>
                <Input
                  value={settings.city}
                  onChange={(e) => setSettings({ ...settings, city: e.target.value })}
                  placeholder="Musterstadt"
                  data-testid="company-city-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Arbeitskosten */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Euro className="w-5 h-5" />
              Arbeitskosten
            </CardTitle>
            <CardDescription>
              Ihr Werkstatt-Stundensatz wird für die automatische Berechnung der Arbeitskosten in Aufträgen und PDFs verwendet (netto, ohne MwSt).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Euro className="w-3 h-3" /> Stundensatz (netto) in €
                </Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={settings.stundenlohn}
                  onChange={(e) => setSettings({ ...settings, stundenlohn: parseFloat(e.target.value) || 0 })}
                  placeholder="z.B. 100.00"
                  data-testid="company-stundenlohn-input"
                />
                <p className="text-xs text-muted-foreground">
                  Gesamt brutto (inkl. {settings.tax_rate}% MwSt): <span className="font-medium text-foreground">{(settings.stundenlohn * (1 + settings.tax_rate / 100)).toFixed(2)} €/h</span>
                </p>
              </div>
              <div className="space-y-2">
                <Label>MwSt-Satz (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="50"
                  step="0.1"
                  value={settings.tax_rate}
                  onChange={(e) => setSettings({ ...settings, tax_rate: parseFloat(e.target.value) || 0 })}
                  placeholder="z.B. 19"
                  data-testid="company-tax-rate-input"
                />
                <p className="text-xs text-muted-foreground">
                  Wird in Aufträgen, Zusammenfassungen und PDFs verwendet (Deutschland: 19%, Österreich: 20%, andere Länder variieren).
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary gap-2"
          data-testid="save-settings-button"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Wird gespeichert...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Einstellungen speichern
            </>
          )}
        </Button>
      </div>

      {/* Developer Credit */}
      <div className="text-center pt-4 border-t">
        <p className="text-xs text-muted-foreground">
          Powered by <span className="font-medium">VyWork</span>
        </p>
      </div>
    </div>
  );
};

export default CompanySettingsPage;
